import React, { useEffect, useState } from "react";
import "./DepositApp.css";
import useAxiosHelper from "../../Common/AxiosHelper";
import { ApiPaths } from "../../Config/ApiPath";
import { BasicInfo } from "../../Config/BasicInfo";
import CopyFromtag from "../../Common/CopyFromtag";
import QRCode from "react-qr-code";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import usdtLogo from "../../Images/tether-usdt-logo.png";
import {
  ArrowDownToLine,
  Copy,
  Check,
  ShieldAlert,
  Share2,
  RefreshCw,
  Inbox,
  ChevronRight,
  Radio,
  PartyPopper,
  ShoppingBag,
} from "lucide-react";

// Deposits are credited to the Fund Wallet as "generate_fund" transactions
// (SERVICES/crypto/PaymentService.js). Opening this page asks the API for the
// user's address, which also starts its payment check.
const DEPOSIT_SOURCE = "generate_fund";

const money = (n) =>
  `$${(parseFloat(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PaymentWithCrypto = () => {
  const { AxiosGet } = useAxiosHelper();
  const navigate = useNavigate();
  const [bep20Address, setBep20Address] = useState("");
  const [payableAmount, setPayableAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [recent, setRecent] = useState(null); // null = loading
  const [justReceived, setJustReceived] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setPayableAmount(urlParams.get("amount") || "");
    FetchPaymentDetails();
    fetchRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // DepositNotifier dispatches this when the socket reports a credited deposit
  useEffect(() => {
    const onDeposit = () => {
      setJustReceived(true);
      fetchRecent();
    };
    window.addEventListener("deposit_success", onDeposit);
    return () => window.removeEventListener("deposit_success", onDeposit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const FetchPaymentDetails = async () => {
    try {
      setLoading(true);
      const res = await AxiosGet(ApiPaths.getPaymentAddress);
      setBep20Address(res?.data?.bep20Address || "");
    } catch (error) {
      console.error("Error fetching crypto payment details:", error);
    } finally {
      setLoading(false);
    }
  };

  async function fetchRecent() {
    try {
      const q = new URLSearchParams({ page: 1, limit: 5, source: DEPOSIT_SOURCE });
      const res = await AxiosGet(`${ApiPaths.getPaymentTransaction}?${q.toString()}`);
      setRecent(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      BasicInfo.isDebug && console.log(e);
      setRecent([]);
    }
  }

  // shared helper: clipboard API, falling back to execCommand when it is blocked
  const copyWalletAddress = () => {
    CopyFromtag("copyWalletAddress");
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1500);
  };

  const shareAddress = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "My USDT (BEP-20) deposit address", text: bep20Address });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    copyWalletAddress();
  };

  return (
    <section className="dpPage">
      {justReceived && (
        <div className="dpReceived" role="status">
          <span>
            <PartyPopper size={18} />
          </span>
          <div>
            <b>Deposit received</b>
            <small>It's now in your Fund Wallet.</small>
          </div>
          <button type="button" onClick={() => navigate("/package")}>
            <ShoppingBag size={14} /> Buy package
          </button>
        </div>
      )}

      {/* ---------- Address card ---------- */}
      <div className="dpCard">
        <div className="dpCardTop">
          <span className="dpEyebrow">
            <ArrowDownToLine size={13} /> Deposit
          </span>
          <span className="dpNetwork">
            <img src={usdtLogo} alt="" /> USDT · BEP-20
          </span>
        </div>

        {payableAmount ? (
          <div className="dpAmount">
            <small>Amount to send</small>
            <b>
              {payableAmount} <em>USDT</em>
            </b>
          </div>
        ) : (
          <div className="dpAmount">
            <small>Send any amount · minimum 5 USDT</small>
          </div>
        )}

        {loading ? (
          <div className="dpQrWrap">
            <div className="dpQrSkel" />
            <p className="dpQrHint">Preparing your deposit address…</p>
          </div>
        ) : bep20Address ? (
          <>
            <div className="dpQrWrap">
              <div className="dpQr">
                <QRCode value={bep20Address} size={188} bgColor="#ffffff" fgColor="#090e2b" level="H" />
                <img src={usdtLogo} alt="" className="dpQrLogo" />
              </div>
              <p className="dpQrHint">Scan with your wallet app</p>
            </div>

            <div className="dpAddr">
              <small>Your deposit address</small>
              <code id="copyWalletAddress">{bep20Address}</code>
            </div>

            <div className="dpActions">
              <button type="button" className={`dpBtn primary ${isCopied ? "isDone" : ""}`} onClick={copyWalletAddress}>
                {isCopied ? <Check size={17} /> : <Copy size={17} />}
                {isCopied ? "Copied" : "Copy address"}
              </button>
              <button type="button" className="dpBtn ghost" onClick={shareAddress} aria-label="Share address">
                <Share2 size={17} />
              </button>
            </div>

            <div className="dpLive">
              <Radio size={14} />
              Watching for your payment — it's credited automatically.
            </div>
          </>
        ) : (
          <div className="dpError">
            <ShieldAlert size={22} />
            <b>No deposit address available</b>
            <small>Please try again in a moment.</small>
            <button type="button" onClick={FetchPaymentDetails}>
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        )}
      </div>

      {/* ---------- Warning ---------- */}
      <div className="dpWarn">
        <ShieldAlert size={18} />
        <span>
          Only send <b>USDT on BNB Smart Chain (BEP-20)</b>. Other coins or networks can't be recovered.
        </span>
      </div>

      {/* ---------- Steps ---------- */}
      <div className="dpSteps">
        <h2>How to deposit</h2>
        <ol>
          <li>
            <span>1</span>
            <p>Copy the address or scan the QR code.</p>
          </li>
          <li>
            <span>2</span>
            <p>
              In your wallet (Trust Wallet, Binance, MetaMask…) choose <b>Send USDT</b> on the <b>BEP-20</b> network.
            </p>
          </li>
          <li>
            <span>3</span>
            <p>Paste the address, enter the amount and confirm. Funds land in your Fund Wallet.</p>
          </li>
        </ol>
      </div>

      {/* ---------- Recent deposits ---------- */}
      <div className="dpSection">
        <div className="dpSectionHead">
          <h2>Recent deposits</h2>
          <button type="button" onClick={() => navigate("/transaction_history")}>
            All activity <ChevronRight size={15} />
          </button>
        </div>
        {recent === null ? (
          <div className="dpList">
            {[0, 1].map((i) => (
              <div key={i} className="dpRow dpSkel">
                <span className="a" />
                <span className="b" />
              </div>
            ))}
          </div>
        ) : recent.length === 0 ? (
          <div className="dpEmpty">
            <Inbox size={20} />
            <span>No deposits yet. Your first deposit will appear here.</span>
          </div>
        ) : (
          <div className="dpList">
            {recent.map((t, i) => (
              <div key={t?._id || i} className="dpRow">
                <span className="dpRowIcon">
                  <ArrowDownToLine size={16} />
                </span>
                <span className="dpRowText">
                  <b>USDT deposit</b>
                  <small>{t?.time ? moment(t.time).format("DD MMM YYYY, hh:mm A") : "—"}</small>
                </span>
                <span className="dpRowRight">
                  <b>+{money(t?.amount)}</b>
                  <small>Fund Wallet</small>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PaymentWithCrypto;
